import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import RightsGrid from './components/RightsGrid';
import CanCannot from './components/CanCannot';
import LicenseFAQ from './components/LicenseFAQ';
import AnimatedSection from '../../components/base/AnimatedSection';

export default function LicensingPage() {
  const navigate = useNavigate();

  const handleBrowseBeats = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('beats');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar cartCount={0} onCartClick={() => {}} />

      {/* ─── HERO ─────────────────────────────────────── */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden bg-gray-950">
        <img
          src="https://public.readdy.ai/ai/img_res/88d89a4ef54356d1637a21d6701a1442.jpg"
          alt="Studio background"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/60 to-gray-950/20"></div>

        {/* Back breadcrumb */}
        <div className="absolute top-24 left-0 right-0 z-10">
          <div className="max-w-7xl mx-auto px-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 w-full">
          <AnimatedSection animation="fade-up" delay={100}>
            <div className="inline-block px-4 py-2 border border-white/20 rounded-full text-xs font-semibold text-gray-300 mb-6 tracking-widest uppercase">
              Exclusive License Agreement
            </div>
            <h1 className="text-7xl font-bold text-white leading-tight mb-6 max-w-3xl">
              Own Your Beat.<br />Own Your Sound.
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl leading-relaxed mb-8">
              Every beat sold at NCMG comes with a full exclusive license — the most complete form of music ownership available. No shared rights, no expiration, no limits.
            </p>
            <div className="flex items-center gap-8">
              <button
                onClick={handleBrowseBeats}
                className="px-8 py-4 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Beats
              </button>
              <a
                href="#whats-included"
                className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-medium transition-colors cursor-pointer"
              >
                Read the full breakdown
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── SUMMARY STATS ────────────────────────────── */}
      <section id="whats-included" className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '100%', label: 'Yours to Keep', sub: 'License never expires' },
              { number: '95%', label: 'Royalty Share', sub: 'You keep 95% of all earnings' },
              { number: '∞', label: 'Streams & Sales', sub: 'Zero revenue cap' },
              { number: '1', label: 'Artist Only', sub: 'Beat removed from store permanently' },
            ].map((stat, i) => (
              <AnimatedSection key={i} animation="fade-up" delay={i * 100} className="text-center">
                <div className="text-5xl font-extrabold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-sm font-bold text-gray-800 mb-0.5">{stat.label}</div>
                <div className="text-xs text-gray-500">{stat.sub}</div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── RIGHTS GRID ──────────────────────────────── */}
      <RightsGrid />

      {/* ─── CAN / CANNOT ─────────────────────────────── */}
      <CanCannot />

      {/* ─── ROYALTY EXPLAINER ────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="slide-left" delay={100}>
              <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-6 tracking-widest uppercase">
                Royalty Structure
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                The 5% Producer<br />Royalty, Explained.
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                When your song earns performance royalties — from radio play, streaming, or live performances — those royalties are collected through a Performing Rights Organization (PRO) like ASCAP, BMI, or SESAC.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                As the producer of the instrumental, NCMG retains a <strong className="text-gray-900">5% share</strong> of those PRO-collected royalties. This is completely standard practice across the music industry — from independent producers to major label deals.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This does <strong className="text-gray-900">not</strong> affect your streaming income, digital sales, sync deal fees, or any direct revenue you generate. You keep 95% of everything.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="slide-right" delay={250}>
              <div className="space-y-4">
                {[
                  { label: 'Your streaming revenue', percent: 100, keep: true },
                  { label: 'Your digital sales income', percent: 100, keep: true },
                  { label: 'Your sync licensing fees', percent: 100, keep: true },
                  { label: 'PRO-collected performance royalties', percent: 95, keep: true },
                  { label: "Producer's PRO royalty share", percent: 5, keep: false },
                ].map((row, i) => (
                  <div key={i} className="bg-white rounded-xl px-6 py-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{row.label}</span>
                      <span className={`text-sm font-bold ${row.keep ? 'text-gray-900' : 'text-gray-400'}`}>
                        {row.keep ? `You keep ${row.percent}%` : `Producer gets ${row.percent}%`}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${row.keep ? 'bg-gray-900' : 'bg-gray-300'}`}
                        style={{ width: `${row.percent}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-up" className="text-center mb-16">
            <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-4 tracking-widest uppercase">
              How It Works
            </div>
            <h2 className="text-6xl font-bold text-gray-900 mb-4 leading-tight">
              From Cart to<br />Cleared in Minutes.
            </h2>
          </AnimatedSection>

          <div className="grid lg:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                icon: 'ri-headphone-line',
                title: 'Find Your Beat',
                desc: 'Browse the catalogue, preview every track in full, and select the instrumental that fits your vision.',
              },
              {
                step: '02',
                icon: 'ri-shopping-cart-line',
                title: 'Add to Cart & Pay',
                desc: 'Secure checkout powered by Stripe. Your beat is immediately placed on hold and removed from public sale.',
              },
              {
                step: '03',
                icon: 'ri-mail-send-line',
                title: 'Receive Files + License',
                desc: <>Your WAV file and exclusive license agreement are delivered to your email instantly after payment confirms. Need an MP3?{' '}<a href="sms:+15716995343" className="text-gray-400 underline whitespace-nowrap hover:text-gray-300 transition-colors">Text +1 (571)-699-5343</a>{' '}and we&apos;ll send it as soon as possible.</>,
              },
              {
                step: '04',
                icon: 'ri-music-2-line',
                title: 'Release Your Music',
                desc: 'Record, release, distribute, and perform. Your exclusive license covers everything from day one.',
              },
            ].map((step, i) => (
              <AnimatedSection key={i} animation="fade-up" delay={i * 120} className="relative">
                <div className="text-7xl font-extrabold text-gray-100 mb-4 leading-none select-none">{step.step}</div>
                <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-xl mb-4">
                  <i className={`${step.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-10 right-0 translate-x-1/2 z-10">
                    <i className="ri-arrow-right-line text-gray-300 text-2xl"></i>
                  </div>
                )}
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────── */}
      <LicenseFAQ />

      {/* ─── CTA ──────────────────────────────────────── */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection animation="fade-up">
            <div className="inline-block px-4 py-2 border border-white/20 rounded-full text-xs font-semibold text-gray-400 mb-6 tracking-widest uppercase">
              Ready to Own Your Sound?
            </div>
            <h2 className="text-6xl font-bold text-white mb-6 leading-tight">
              Your next hit starts<br />with the right beat.
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
              Browse our catalogue of exclusive beats. Every purchase is backed by the full licensing rights detailed on this page.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <button
                onClick={handleBrowseBeats}
                className="px-10 py-4 bg-white text-gray-900 rounded-full font-bold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse All Beats
              </button>
              <a
                href="/#booking"
                className="px-10 py-4 border border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                Book a Session
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
